import { CtaArrow, ctaClasses } from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { Container, PageHero } from '@/components/marketing-ui';
import { useT } from '@/core/I18nProvider';
import { useDocumentTitle } from '@/lib/seo/use-document-title';

import type { ReactElement } from 'react';

/**
 * Marketing 404 page (Phase 7 v1.1 iter 295).
 *
 * Closes the UX gap where `App.tsx` previously routed unknown paths
 * through `<Navigate to="/" replace />` — a silent teleport that
 * gave the customer no indication they hit a bad URL. With 11 routes
 * now live (home + features + the trust pages), the
 * surface is large enough that a typo'd link from outreach or a
 * stale doc anchor should land here, not on home.
 *
 * The page is a thin shell: clear h1 + literal "404" for screen-
 * reader / scanner clarity + 3 deep-links to the canonical routes
 * a mistype was most likely heading toward (home, features, trust).
 * No analytics fingerprinting, no auto-redirect — just an honest
 * dead-end with recovery affordances.
 */
export function NotFound(): ReactElement {
  const { t } = useT();
  useDocumentTitle(t('notfound.documentTitle'));
  return (
    <main id="main" tabIndex={-1} className="min-h-screen bg-[color:var(--bd-page)]">
      {/* The 404 renders the brand gradient at display size — the one place on
          the site where a big graphic device costs nothing, because there is no
          content for it to compete with. It is `aria-hidden` and the status is
          carried for assistive tech by the `sr-only` prefix below, so the
          decoration is decoration. */}
      <PageHero
        eyebrow={
          <span
            aria-hidden="true"
            className="bd-grad-text text-[3.5rem] font-semibold leading-none tracking-[-0.05em]"
          >
            404
          </span>
        }
        title={t('notfound.heading')}
        description={
          <>
            <span className="sr-only">{t('notfound.statusPrefix')}</span>
            {t('notfound.description')}
          </>
        }
      />

      <Container as="section" width="prose" aria-label={t('notfound.tryLabel')} className="py-14">
        <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:justify-center">
          <Link to="/" className={ctaClasses({ size: 'md' })}>
            <CtaArrow direction="back" />
            {t('notfound.backHome')}
          </Link>
          <Link to="/features" className={ctaClasses({ variant: 'secondary', size: 'md' })}>
            {t('notfound.seeFeatures')}
          </Link>
          <Link to="/trust" className={ctaClasses({ variant: 'secondary', size: 'md' })}>
            {t('notfound.trustCenter')}
          </Link>
        </div>
      </Container>
    </main>
  );
}
